import { useState, useContext, useEffect } from "react";
import { useNavigate } from "react-router";
import axios from 'axios';

import { AppContext } from '../../context.js';

import { ListCheck, MoreVertical, ChevronDown, Refresh, ArrowRight, CalendarEdit } from "../../files/icons/index.js";

import { Status, Button } from './component.js';
import Editor from "./editor.js";
import Drop from "../components/drop.js";
import { DatePicker } from "../components/datePicker.js";
import RecurrencePicker from "../components/recurrencePicker.js";
import PhysicianPicker from "../components/physicianPicker.js";
import { FullName } from '../../utils/string.js';
import { isCognitiveStagingTemplate } from '../../utils/assessments.js';

import { TimestampToDate, DateToTimestamp, DateToFormat, DatetimeToDate, FirstDayOfMonth, LastDayOfMonth, InRangeUTC } from "../../utils/datetime.js";

function AssessmentCard(props) {
  const context = useContext(AppContext);

  const { assessment: initialAssessment, users, settings, onFormClick, onStatusChange = null } = props;

  const [assessment, setAssessment] = useState(initialAssessment);

  useEffect(() => {
    setAssessment(initialAssessment);
  }, [initialAssessment]);

  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);

  const [closeAssessmentStatusDrop, setCloseAssessmentStatusDrop] = useState(0);

  const [changeAssessmentStatusModalVisible, setChangeAssessmentStatusModalVisible] = useState(false);
  const [changeAssessmentStatusModalData, setChangeAssessmentStatusModalData] = useState(null);

  // Update assessment created at
  const [updateAssessmentCreatedAtData, setUpdateAssessmentCreatedAtData] = useState({
    visible: false,
    id: null,
    date: null,
    reason: null,
  });

  // Update assessment closed at
  const [updateAssessmentClosedAtData, setUpdateAssessmentClosedAtData] = useState({
    visible: false,
    id: null,
    date: null,
    reason: null,
  });

  // Functions
  function changeAssessmentStatus(assessmentID, status) {
    if (status.reasons.length > 0) {
      setChangeAssessmentStatusModalData({status, assessmentID});
      setChangeAssessmentStatusModalVisible(true);
      return;
    }

    updateAssessmentStatus(assessmentID, status.id, null);
  }

  function changeAssessmentStatusConfirm(assessmentID, statusID, reasonID) {
    setChangeAssessmentStatusModalVisible(false);
    setChangeAssessmentStatusModalData(null);
    updateAssessmentStatus(assessmentID, statusID, reasonID);
  }

  function updateAssessmentStatus(assessmentID, statusID, reasonID) {
    let u = context.addSpiner();
    axios.post('/api/admin/assessments/updateStatus', {
      id: assessmentID,
      statusID: statusID,
      reasonID: reasonID,
    }).then((response) => {
      setAssessment({
        ...assessment, 
        statusType: response.data.statusType, 
        statusID: response.data.statusID, 
        statusName: response.data.statusName, 
        statusReasonID: response.data.statusReasonID, 
        statusReasonName: response.data.statusReasonName,

        scores: response.data.scores,

        closedAt: response.data.closedAt,
        closedBy: response.data.closedBy,
        closedByName: response.data.closedByName,
      });
      setCloseAssessmentStatusDrop(prev => prev + 1);
      // Canceling/reopening a FAST/CDR flips whether the patient still has an active
      // cognitive staging assessment, so refresh the parent's one-per-patient check.
      if (onStatusChange && isCognitiveStagingTemplate(assessment.templateID)) onStatusChange();
    }).catch((e) => {
      context.addError(e);
      context.removeSpiner(u);
    }).finally(() => {
      context.removeSpiner(u);
    });
  }

  function changeAssessmentTitle(id, text) {
    setAssessment(prevAssessment => ({ ...prevAssessment, title: text }));
  }

  function keyUpAssessmentTitle(e) {
    if (e.key === 'Enter') {
      e.target.blur();
    }
  }

  function updateAssessmentTitle(id) {
    let u = context.addSpiner();
    axios.post('/api/admin/assessments/updateTitle', {
      id: assessment.id,
      title: assessment.title
    }).then((response) => {
      
    }).catch((e) => {
      context.addError(e);
      context.removeSpiner(u);
    }).finally(() => {
      context.removeSpiner(u);
    });
  }

  function updateAssessmentDescription(text, id) {
    if (text === "" || text === "<p><br></p>") {
      text = null;
    }

    let u = context.addSpiner();
    axios.post('/api/admin/assessments/updateDescription', {
      id: assessment.id,
      description: text
    }).then((response) => {
      setAssessment(prevAssessment => ({ ...prevAssessment, description: text }));
    }).catch((e) => {
      context.addError(e);
      context.removeSpiner(u);
    }).finally(() => {
      context.removeSpiner(u);
    });
  }

  function updateAssessmentUser(user, id) {
    let u = context.addSpiner();
    axios.post('/api/admin/assessments/updateUser', {
      id: id,
      userID: user.id
    }).then((response) => {
      setAssessment(prevAssessment => ({ ...prevAssessment, userID: user.id, userName: FullName(user.firstName, user.middleName, user.lastName) }));
    }).catch((e) => {
      context.addError(e);
      context.removeSpiner(u);
    }).finally(() => {
      context.removeSpiner(u);
    });
  }

  function updateAssessmentDeadline(date, id) {
    let u = context.addSpiner();
    axios.post('/api/admin/assessments/updateDeadline', {
      id: id,
      deadline: date
    }).then((response) => {
      setAssessment(prevAssessment => ({ ...prevAssessment, deadlineAt: date }));
    }).catch((e) => {
      context.addError(e);
      context.removeSpiner(u);
    }).finally(() => {
      context.removeSpiner(u);
    });
  }

  function updateAssessmentRecurrence(assessmentID, intervalType, intervalDuration, fromType) {
    let u = context.addSpiner();
    axios.post('/api/admin/assessments/updateRecurrence', {
      id: assessmentID,
      intervalType: intervalType,
      intervalDuration: intervalDuration,
      fromType: fromType,
    }).then((response) => {
      setAssessment(prevAssessment => ({ ...prevAssessment, recurreAt: response.data.recurreAt }));
    }).catch((e) => {
      context.addError(e);
      context.removeSpiner(u);
    }).finally(() => {
      context.removeSpiner(u);
    });
  }

  function updateAssessmentCreatedAt() {
    let u = context.addSpiner();
    axios.post('/api/admin/assessments/updateCreatedAt', {
      id: updateAssessmentCreatedAtData.id,
      createdAt: updateAssessmentCreatedAtData.date,
      reason: updateAssessmentCreatedAtData.reason,
    }).then((response) => {
      setAssessment(prevAssessment => ({ ...prevAssessment, createdAt: updateAssessmentCreatedAtData.date }));
      setUpdateAssessmentCreatedAtData({ visible: false, id: null, date: null, reason: null });
    }).catch((e) => {
      context.addError(e);
      context.removeSpiner(u);
    }).finally(() => {
      context.removeSpiner(u);
    });
  }

  function updateAssessmentClosedAt() {
    let u = context.addSpiner();
    axios.post('/api/admin/assessments/updateClosedAt', {
      id: updateAssessmentClosedAtData.id,
      closedAt: updateAssessmentClosedAtData.date,
      reason: updateAssessmentClosedAtData.reason,
    }).then((response) => {
      setAssessment(prevAssessment => ({ ...prevAssessment, closedAt: updateAssessmentClosedAtData.date }));
      setUpdateAssessmentClosedAtData({ visible: false, id: null, date: null, reason: null });
    }).catch((e) => {
      context.addError(e);
      context.removeSpiner(u);
    }).finally(() => {
      context.removeSpiner(u);
    });
  }

  function renderAssessmentStatus(templateID, statusID) {
    const status = settings.assessmentTemplates.find((t) => t.id === templateID).statuses.find((s) => s.id === statusID);

    if (!status) {
      return (
        <></>
      );
    }

    return (
      <Status color={status.color} muted={true} size={'small'} subtext={status.title} />
    );
  }

  function renderAssessmentNextStatuses(assessmentID, templateID, statusID) {
    const currentStatus = settings.assessmentTemplates.find((t) => t.id === templateID).statuses.find((s) => s.id === statusID);

    if (!currentStatus) {
      return (
        <></>
      );
    }

    if (!currentStatus.next) {
      return (
        <div className="typo-medium-150">
          Task has no next statuses
        </div>
      );
    }

    return settings.assessmentTemplates.find((t) => t.id === templateID).statuses.filter((status) => currentStatus.next.includes(status.id)).map((status) => {
      return (
        <div className="side-item small typo-medium-150" onClick={() => changeAssessmentStatus(assessmentID, status)}>
          To <ArrowRight height={14} width={16} color={'grey'} /> {renderAssessmentStatus(templateID, status.id)}
        </div>
      );
    })
  }

  function renderAssessment() {
    
    var datePickerColor = "grey";
    if (assessment.deadlineAt && assessment.statusType !== "COMPLETED" && assessment.statusType !== "CANCELED") {
      const today = new Date();
      const deadlineDate = new Date(assessment.deadlineAt);
      
      // Helper functions for today's range
      const firstMomentOfToday = new Date(today.getFullYear(), today.getMonth(), today.getDate(), 0, 0, 0, 0);
      const lastMomentOfToday = new Date(today.getFullYear(), today.getMonth(), today.getDate(), 23, 59, 59, 999);
      
      // Helper functions for tomorrow's range
      const tomorrow = new Date(today.getFullYear(), today.getMonth(), today.getDate() + 1);
      const firstMomentOfTomorrow = new Date(tomorrow.getFullYear(), tomorrow.getMonth(), tomorrow.getDate(), 0, 0, 0, 0);
      const lastMomentOfTomorrow = new Date(tomorrow.getFullYear(), tomorrow.getMonth(), tomorrow.getDate(), 23, 59, 59, 999);
      
      // Check if deadline is tomorrow (today is previous day of deadline)
      if (InRangeUTC(deadlineDate, firstMomentOfTomorrow, lastMomentOfTomorrow)) {
        datePickerColor = "yellow";
      }
      // Check if deadline is today or overdue
      else if (InRangeUTC(deadlineDate, firstMomentOfToday, lastMomentOfToday) || new Date().getTime() > deadlineDate.getTime()) {
        datePickerColor = "red";
      }
    }
      
    return (
      <div key={"assessment_" + assessment.id} id={"assessment_" + assessment.id} className="card mb-2">
        <div className="card-header sm">
          <div className="d-flex justify-content-between align-items-center">
            <input type="text" className="input borderless typo-strong-200 w-100" placeholder="Title..." value={assessment.title} onChange={(e) => changeAssessmentTitle(assessment.id, e.target.value)} onBlur={() => updateAssessmentTitle(assessment.id)} onKeyUp={(e) => keyUpAssessmentTitle(e)} />
            <div className="d-flex justify-content-end align-items-center w-25">
              {renderAssessmentStatus(assessment.templateID, assessment.statusID)}
              <Drop close={closeAssessmentStatusDrop} side={"right"} className="ms-1">
                <div className="item rect">
                  <MoreVertical height={14} width={16} color={'grey'} />
                </div>
                <div className="card" style={{width: 200}}>
                  <div className="card-header sm">
                    {!isCognitiveStagingTemplate(assessment.templateID) && (
                      <>
                        <div className="typo-normal-150 text-muted">Change status:</div>
                        {renderAssessmentNextStatuses(assessment.id, assessment.templateID, assessment.statusID)}
                      </>
                    )}
                    <div className="typo-normal-150 text-muted">Change dates:</div>
                    <div className="side-item small typo-medium-150" onClick={() => setUpdateAssessmentCreatedAtData({ visible: true, id: assessment.id, date: assessment.createdAt, reason: null })}>
                      Change created at date
                    </div>
                    {assessment.closedAt && (
                      <div className="side-item small typo-medium-150" onClick={() => setUpdateAssessmentClosedAtData({ visible: true, id: assessment.id, date: assessment.closedAt, reason: null })}>
                        Change closed at date
                      </div>
                    )}
                    <div className="typo-normal-150 text-muted">Other actions:</div>
                    <div className="side-item small nowrap typo-medium-150" onClick={() => navigate("/export?assessmentID=" + assessment.id)}>
                      <div className="typo-normal-150">Export Assessment</div>
                    </div>
                  </div>
                </div>
              </Drop>
            </div>
          </div>
          
          <Editor 
            placeholder="Assessment description..."
            value={assessment.description}
            onChangeID={assessment.id}
            onBlur={updateAssessmentDescription}
            toolbar="limited"
            className="text-editor borderless"
          />

          {(assessment.scores && assessment.scores.length > 0) && (
            <div className="mt-1">
              {assessment.scores.map((score, ai) => {
                return (
                  <div key={"assessment_"+assessment.id+"_score_" + score.id} className="item grey me-2">
                    <div className="d-flex justify-content-start align-items-center">
                      <div>
                        <div className="typo-normal-150">{score.title}</div>
                        {score.description && (
                          <div className="typo-normal-150">{score.description}</div>
                        )}
                      </div>
                      <div className="typo-strong-150 ms-2">{score.score}</div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          <div className="d-flex justify-content-start align-items-center mt-2">
            {/* <div className="d-flex justify-content-start align-items-center me-3">
              <Hashtag height={16} width={16} background={'none'} fill={'grey'} /> <span className="typo-normal-150">{assessment.templateName}</span>
            </div> */}

            <div className="d-flex justify-content-start align-items-center me-3">
              <span className="typo-normal-150">created at {DateToFormat(DatetimeToDate(assessment.createdAt), context.dateFormat)} {DateToFormat(DatetimeToDate(assessment.createdAt), context.timeFormat === "us" ? "AMPM" : "HH:mm")}</span>
            </div>

            {assessment.closedAt && (
              <div className="d-flex justify-content-start align-items-center me-3">
                <span className="typo-normal-150">closed at {DateToFormat(DatetimeToDate(assessment.closedAt), context.dateFormat)} {DateToFormat(DatetimeToDate(assessment.closedAt), context.timeFormat === "us" ? "AMPM" : "HH:mm")}</span>
              </div>
            )}
          </div>

          <div className="d-flex justify-content-between align-items-center mt-2">
            <div className="d-flex justify-content-start align-items-center w-100">
              <div onClick={() => onFormClick && onFormClick(assessment)}>
                <div className="item grey">
                  <ListCheck height={16} width={16} color={'grey'} />
                  <span className="typo-normal-150">Open Assessment</span>
                  <ArrowRight height={14} width={16} color={'grey'} />
                </div>
              </div>

              <PhysicianPicker
                physicianList={users}
                physicianID={assessment.userID}
                onChange={updateAssessmentUser}
                onChangeID={assessment.id}
                // leftIcon={<UserEdit height={16} width={16} background={'none'} fill={'grey'} />}
                rightIcon={<ChevronDown height={16} width={16} color={'grey'} />}
                className="ms-2 me-2"
              />

              {/* // */}
              <DatePicker
                date={assessment.deadlineAt}
                onChange={updateAssessmentDeadline}
                onChangeID={assessment.id}
                leftIcon={<CalendarEdit height={16} width={16} color={'grey'} />}
                rightIcon={<ChevronDown height={16} width={16} color={'grey'} />}
                className={"me-2 " + datePickerColor}
              />

              <RecurrencePicker
                recurreAt={assessment.recurreAt}
                onChange={updateAssessmentRecurrence}
                onChangeID={assessment.id}
                leftIcon={<Refresh height={16} width={16} color={'grey'} />}
                rightIcon={<ChevronDown height={16} width={16} color={'grey'} />}
                className="me-2"
              />
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <></>
    );
  }

  return (
    <>
      {updateAssessmentCreatedAtData.visible && (
        <div className="fixed-bottom modal-bg" onClick={(e) => setUpdateAssessmentCreatedAtData({ visible: false })}>
          <div className="container-fluid" style={{paddingTop: '5%'}}>
            <div className="row">
              <div className="col-xs-10 col-xs-offset-1 col-lg-4 col-lg-offset-4">
                <div className="card shadowmore" onClick={(e) => {e.stopPropagation()}}>
                  <div className="card-header">
                    <span className="typo-header-3xLarge">Update Assessment Creation Date</span><br/>
                    <span className="typo-normal-175">Select the correct date and reason of creation and click update button</span>
                  </div>
                  <div className="card-content">
                    <div className="d-flex justify-content-start align-items-center">
                      <DatePicker
                        date={updateAssessmentCreatedAtData.date}
                        onChange={(date) => setUpdateAssessmentCreatedAtData({...updateAssessmentCreatedAtData, date: date})}
                        onChangeID={null}
                        leftIcon={<CalendarEdit height={16} width={16} color={'grey'} />}
                        rightIcon={<ChevronDown height={16} width={16} color={'grey'} />}
                        className="me-2"
                      />

                      <input type="text" className="input borderless typo-normal-150 w-100 ms-2" placeholder="Reason..." value={updateAssessmentCreatedAtData.reason} onChange={(e) => setUpdateAssessmentCreatedAtData({...updateAssessmentCreatedAtData, reason: e.target.value})} />
                    </div>
                  </div>  
                  <div className="card-content text-end">
                    <Button onClick={updateAssessmentCreatedAt} color="blue" text="Update" />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {updateAssessmentClosedAtData.visible && (
        <div className="fixed-bottom modal-bg" onClick={(e) => setUpdateAssessmentClosedAtData({ visible: false })}>
          <div className="container-fluid" style={{paddingTop: '5%'}}>
            <div className="row">
              <div className="col-xs-10 col-xs-offset-1 col-lg-4 col-lg-offset-4">
                <div className="card shadowmore" onClick={(e) => {e.stopPropagation()}}>
                  <div className="card-header">
                    <span className="typo-header-3xLarge">Update Assessment Completion Date</span><br/>
                    <span className="typo-normal-175">Select the correct date and reason of completion and click update button</span>
                  </div>
                  <div className="card-content">
                    <div className="d-flex justify-content-start align-items-center">
                      <DatePicker
                        date={updateAssessmentClosedAtData.date}
                        onChange={(date) => setUpdateAssessmentClosedAtData({...updateAssessmentClosedAtData, date: date})}
                        onChangeID={null}
                        leftIcon={<CalendarEdit height={16} width={16} color={'grey'} />}
                        rightIcon={<ChevronDown height={16} width={16} color={'grey'} />}
                        className="me-2"
                      />

                      <input type="text" className="input borderless typo-normal-150 w-100 ms-2" placeholder="Reason..." value={updateAssessmentClosedAtData.reason} onChange={(e) => setUpdateAssessmentClosedAtData({...updateAssessmentClosedAtData, reason: e.target.value})} />
                    </div>
                  </div>
                  <div className="card-content text-end">
                    <Button onClick={updateAssessmentClosedAt} color="blue" text="Update" />
                  </div>  
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {renderAssessment()}
    </>
  );
}

export default AssessmentCard;